import { EditorState, StateField, StateEffect } from '@codemirror/state';
import { EditorView, keymap, Decoration, ViewPlugin, WidgetType } from '@codemirror/view';
import { markdown } from '@codemirror/lang-markdown';
import { GFM } from '@lezer/markdown';
import { syntaxTree } from '@codemirror/language';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import katex from 'katex';
import { buildTaskOutline } from './task-hierarchy.js';

// Task Checkbox Widget
class TaskWidget extends WidgetType {
  constructor(checked, pos, onToggle) {
    super();
    this.checked = checked;
    this.pos = pos;
    this.onToggle = onToggle;
  }
  eq(other) { return other.checked === this.checked && other.pos === this.pos; }
  toDOM() {
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'task-checkbox';
    input.checked = this.checked;
    input.addEventListener('click', (e) => {
      e.stopPropagation();
      if (navigator.vibrate) navigator.vibrate(12);
      if (this.onToggle) this.onToggle(this.pos, !this.checked);
    });
    return input;
  }
}

// Math Widget
class MathWidget extends WidgetType {
  constructor(raw, isBlock) {
    super();
    this.raw = raw;
    this.isBlock = isBlock;
  }
  eq(other) { return other.raw === this.raw && other.isBlock === this.isBlock; }
  toDOM() {
    const span = document.createElement('span');
    span.className = this.isBlock ? 'cm-math-block' : 'cm-math-inline';
    try {
      katex.render(this.raw, span, { displayMode: this.isBlock, throwOnError: false });
    } catch (err) {
      span.textContent = this.raw;
    }
    return span;
  }
}

// Link Pill Widget
class LinkPillWidget extends WidgetType {
  constructor(title, url) {
    super();
    this.title = title;
    this.url = url;
  }
  eq(other) { return other.title === this.title && other.url === this.url; }
  toDOM() {
    const a = document.createElement('a');
    a.className = 'cm-link-pill';
    a.href = this.url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.innerHTML = `${escapeHtml(this.title)} <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>`;
    a.onclick = (e) => e.stopPropagation();
    return a;
  }
}

// Project Pill Widget — tap navigates to the note whose first line matches
// the project name (a lightweight stand-in for desktop's LinkedTaskManager
// project resolution), via onProjectClick.
class ProjectPillWidget extends WidgetType {
  constructor(projectName, onProjectClick) {
    super();
    this.projectName = projectName;
    this.onProjectClick = onProjectClick;
  }
  eq(other) { return other.projectName === this.projectName; }
  toDOM() {
    const span = document.createElement('span');
    span.className = 'cm-project-pill';
    span.textContent = `-> ${this.projectName}`;
    span.onclick = (e) => {
      e.stopPropagation();
      if (this.onProjectClick) this.onProjectClick(this.projectName);
    };
    return span;
  }
}

// Collapse/Expand Chevron Widget — shown before a parent task's checkbox.
class CollapseChevronWidget extends WidgetType {
  constructor(linePos, isCollapsed, view) {
    super();
    this.linePos = linePos;
    this.isCollapsed = isCollapsed;
    this.view = view;
  }
  eq(other) { return other.linePos === this.linePos && other.isCollapsed === this.isCollapsed; }
  toDOM() {
    const btn = document.createElement('span');
    btn.className = 'cm-task-chevron' + (this.isCollapsed ? ' collapsed' : '');
    btn.textContent = '▸';
    btn.onclick = (e) => {
      e.stopPropagation();
      if (navigator.vibrate) navigator.vibrate(8);
      this.view.dispatch({ effects: toggleCollapseEffect.of(this.linePos) });
    };
    return btn;
  }
}

// Collapsed task hierarchy — desktop's collapseRenderField equivalent. Must
// live in a StateField, not the ViewPlugin above: a Decoration.replace that
// spans a line break needs block:true from a StateField (root CLAUDE.md
// gotcha #2), a ViewPlugin throws "line break" for that.
export const toggleCollapseEffect = StateEffect.define();

export const collapsedTaskLinesField = StateField.define({
  create() { return new Set(); },
  update(collapsed, tr) {
    let next = new Set();
    for (const pos of collapsed) next.add(tr.changes.mapPos(pos));
    for (const effect of tr.effects) {
      if (effect.is(toggleCollapseEffect)) {
        if (next.has(effect.value)) next.delete(effect.value);
        else next.add(effect.value);
      }
    }
    return next;
  }
});

function buildCollapseDecorations(state) {
  const collapsed = state.field(collapsedTaskLinesField, false);
  if (!collapsed || collapsed.size === 0) return Decoration.none;
  const outline = buildTaskOutline(state.doc);
  const decos = [];
  for (const pos of collapsed) {
    const node = outline.find(n => n.from === pos);
    if (node && node.hasChildren && node.childrenEndPos != null && node.to < node.childrenEndPos) {
      decos.push(Decoration.replace({ block: true }).range(node.to, node.childrenEndPos));
    }
  }
  return Decoration.set(decos, true);
}

export const collapseRenderField = StateField.define({
  create(state) { return buildCollapseDecorations(state); },
  update(decos, tr) {
    if (tr.docChanged || tr.effects.some(e => e.is(toggleCollapseEffect))) {
      return buildCollapseDecorations(tr.state);
    }
    return decos;
  },
  provide(field) { return EditorView.decorations.from(field); }
});

// Transient "jump here" highlight after task-reference navigation.
const flashLineEffect = StateEffect.define();
const flashLineField = StateField.define({
  create() { return null; },
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(flashLineEffect)) return effect.value;
    }
    return value && tr.docChanged ? { pos: tr.changes.mapPos(value.pos) } : value;
  },
  provide(field) {
    return EditorView.decorations.from(field, value =>
      value ? Decoration.set([Decoration.line({ class: 'cm-task-flash' }).range(value.pos)]) : Decoration.none
    );
  }
});

// Horizontal Rule Widget
class HRWidget extends WidgetType {
  toDOM() {
    const hr = document.createElement('hr');
    hr.className = 'cm-hr-widget';
    return hr;
  }
}

// Bullet List Marker Widget
class BulletWidget extends WidgetType {
  toDOM() {
    const span = document.createElement('span');
    span.className = 'cm-list-bullet';
    span.textContent = '•';
    return span;
  }
}

// Table Row Widget — renders one GFM table row (header or body) as a flex row.
class TableRowWidget extends WidgetType {
  constructor(cells, isHeader) {
    super();
    this.cells = cells;
    this.isHeader = isHeader;
  }
  eq(other) {
    return other.isHeader === this.isHeader && other.cells.join(' ') === this.cells.join(' ');
  }
  toDOM() {
    const row = document.createElement('span');
    row.className = 'cm-table-row' + (this.isHeader ? ' cm-table-header' : '');
    this.cells.forEach(cellText => {
      const cell = document.createElement('span');
      cell.className = 'cm-table-cell';
      cell.textContent = cellText;
      row.appendChild(cell);
    });
    return row;
  }
}

// Recall Tag Widget (#recall)
class RecallTagWidget extends WidgetType {
  toDOM() {
    const span = document.createElement('span');
    span.className = 'cm-recall-tag-pill';
    span.textContent = '🧠 Recall';
    return span;
  }
}

// Task Reference Pill Widget (trailing ^t-<id> on a task line) — tap navigates
// to whichever note contains the referenced task, via onTaskRefClick.
class TaskReferencePillWidget extends WidgetType {
  constructor(taskId, onTaskRefClick) {
    super();
    this.taskId = taskId;
    this.onTaskRefClick = onTaskRefClick;
  }
  eq(other) { return other.taskId === this.taskId; }
  toDOM() {
    const span = document.createElement('span');
    span.className = 'cm-task-ref-pill';
    span.textContent = '#' + this.taskId.slice(0, 4);
    span.onclick = (e) => {
      e.stopPropagation();
      if (this.onTaskRefClick) this.onTaskRefClick(this.taskId);
    };
    return span;
  }
}

function parseTableRowCells(rawRow) {
  let text = rawRow.trim();
  if (text.startsWith('|')) text = text.slice(1);
  if (text.endsWith('|')) text = text.slice(0, -1);
  return text.split('|').map(c => c.trim());
}

function isTableDelimiterRow(cells) {
  return cells.length > 0 && cells.every(c => /^:?-+:?$/.test(c));
}

function escapeHtml(str) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Full Markdown Decorator Plugin
function createMarkdownLivePlugin(onToggleTask, onTaskRefClick, onProjectClick) {
  return ViewPlugin.fromClass(class {
    constructor(view) {
      this.decorations = this.buildDecos(view);
    }
    update(update) {
      if (update.docChanged || update.viewportChanged || update.selectionSet) {
        this.decorations = this.buildDecos(update.view);
      }
    }
    buildDecos(view) {
      const decos = [];
      const docText = view.state.doc.toString();
      const selection = view.state.selection.main;
      const cursorLineNum = view.state.doc.lineAt(selection.main ? selection.main.from : selection.from).number;
      const lineDecoSet = new Set();

      function addLineDeco(pos, cls) {
        const lineStart = view.state.doc.lineAt(pos).from;
        const key = `${lineStart}:${cls}`;
        if (lineDecoSet.has(key)) return;
        lineDecoSet.add(key);
        decos.push(Decoration.line({ class: cls }).range(lineStart));
      }

      // Obsidian-style marker dimming (matches desktop editor-web's markdown-deco.js):
      // cm-md-marker collapses to font-size:0 everywhere except the cursor's own
      // line, where cm-cursor-line restores it. See theme.css.
      addLineDeco(selection.from, 'cm-cursor-line');

      function addLineRangeDeco(fromPos, toPos, cls) {
        const startLine = view.state.doc.lineAt(fromPos).number;
        const endLine = view.state.doc.lineAt(Math.max(fromPos, toPos - 1)).number;
        for (let ln = startLine; ln <= endLine; ln++) {
          addLineDeco(view.state.doc.line(ln).from, cls);
        }
      }

      // 1. Lezer Syntax Tree iterate for Headings, HeaderMark hiding, and other
      // structural GFM nodes (bold/italic/strikethrough/blockquote/fenced code).
      for (const { from, to } of view.visibleRanges) {
        syntaxTree(view.state).iterate({
          from,
          to,
          enter(node) {
            switch (node.name) {
              case 'ATXHeading1':
                addLineDeco(node.from, 'cm-heading-1');
                break;
              case 'ATXHeading2':
                addLineDeco(node.from, 'cm-heading-2');
                break;
              case 'ATXHeading3':
                addLineDeco(node.from, 'cm-heading-3');
                break;
              case 'ATXHeading4':
                addLineDeco(node.from, 'cm-heading-4');
                break;
              case 'ATXHeading5':
              case 'ATXHeading6':
                addLineDeco(node.from, 'cm-heading-5');
                break;

              case 'HeaderMark': {
                const line = view.state.doc.lineAt(node.from);
                // If cursor is NOT on this line, hide the leading # mark + space
                if (line.number !== cursorLineNum) {
                  const charAfter = view.state.sliceDoc(node.to, node.to + 1);
                  const hideTo = (charAfter === ' ' || charAfter === '\t') ? node.to + 1 : node.to;
                  decos.push(Decoration.replace({}).range(node.from, hideTo));
                }
                break;
              }

              case 'StrongEmphasis':
                decos.push(Decoration.mark({ class: 'cm-bold' }).range(node.from, node.to));
                break;
              case 'Emphasis':
                decos.push(Decoration.mark({ class: 'cm-italic' }).range(node.from, node.to));
                break;
              case 'Strikethrough':
                decos.push(Decoration.mark({ class: 'cm-strike' }).range(node.from, node.to));
                break;
              case 'EmphasisMark':
              case 'StrikethroughMark':
                decos.push(Decoration.mark({ class: 'cm-md-marker' }).range(node.from, node.to));
                break;

              case 'Blockquote':
                addLineRangeDeco(node.from, node.to, 'cm-blockquote');
                break;

              case 'FencedCode': {
                const startLine = view.state.doc.lineAt(node.from).number;
                const endLine = view.state.doc.lineAt(Math.max(node.from, node.to - 1)).number;
                for (let ln = startLine; ln <= endLine; ln++) {
                  let cls = 'cm-code-block';
                  if (ln === startLine) cls += ' cm-code-block-first';
                  if (ln === endLine) cls += ' cm-code-block-last';
                  addLineDeco(view.state.doc.line(ln).from, cls);
                }
                break;
              }

              case 'ListMark': {
                const line = view.state.doc.lineAt(node.from);
                const isTaskLine = /^\s*[-*]\s+\[[ xX]\]/.test(line.text);
                if (isTaskLine || line.number === cursorLineNum) break;
                const parentType = node.node.parent?.parent?.type.name;
                if (parentType === 'BulletList') {
                  decos.push(Decoration.replace({ widget: new BulletWidget() }).range(node.from, node.to));
                } else if (parentType === 'OrderedList') {
                  decos.push(Decoration.mark({ class: 'cm-list-number' }).range(node.from, node.to));
                }
                break;
              }

              case 'TableHeader':
              case 'TableRow': {
                const line = view.state.doc.lineAt(node.from);
                if (line.number === cursorLineNum) break;
                const cells = parseTableRowCells(view.state.sliceDoc(node.from, node.to));
                if (isTableDelimiterRow(cells)) break;
                decos.push(Decoration.replace({
                  widget: new TableRowWidget(cells, node.name === 'TableHeader')
                }).range(node.from, node.to));
                break;
              }

              case 'TableDelimiter': {
                const line = view.state.doc.lineAt(node.from);
                if (line.number !== cursorLineNum) {
                  decos.push(Decoration.replace({}).range(node.from, node.to));
                  addLineDeco(line.from, 'cm-table-delimiter-hidden');
                }
                break;
              }
            }
          }
        });
      }

      // Fallback Pass for Headings and HeaderMark hiding when cursor is off line
      const headingRegex = /^(#{1,6})\s+/gm;
      let m;
      while ((m = headingRegex.exec(docText)) !== null) {
        const markFrom = m.index;
        const markTo = markFrom + m[0].length;
        const line = view.state.doc.lineAt(markFrom);
        const level = m[1].length;
        const cls = `cm-heading-${Math.min(level, 3)}`;
        addLineDeco(line.from, cls);

        // Hide leading # mark when cursor is on another line
        if (line.number !== cursorLineNum) {
          decos.push(Decoration.replace({}).range(markFrom, markTo));
        }
      }

      // 2. Task Checkboxes (- [ ] or - [x]) — plus depth indent + collapse
      // chevron for parents (desktop's task hierarchy, see task-hierarchy.js).
      const outline = buildTaskOutline(view.state.doc);
      const collapsedSet = view.state.field(collapsedTaskLinesField, false) || new Set();
      const taskRegex = /^([ \t]*[-*][ \t]+)\[([ xX])\]/gm;
      while ((m = taskRegex.exec(docText)) !== null) {
        const start = m.index + m[1].length;
        const end = start + 3;
        const isChecked = m[2].toLowerCase() === 'x';
        const line = view.state.doc.lineAt(m.index);
        const node = outline.find(n => n.from === line.from);
        if (node) {
          addLineDeco(line.from, `cm-task-nested-${Math.min(node.depth, 3)}`);
          if (node.hasChildren) {
            const isCollapsed = collapsedSet.has(node.from);
            decos.push(Decoration.widget({
              widget: new CollapseChevronWidget(node.from, isCollapsed, view),
              side: -1
            }).range(line.from));
          }
        }
        if (selection.from >= start && selection.to <= end) continue;
        decos.push(Decoration.replace({ widget: new TaskWidget(isChecked, start, onToggleTask) }).range(start, end));
      }

      // 3. Link Pills ([Title](URL))
      const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
      while ((m = linkRegex.exec(docText)) !== null) {
        const start = m.index;
        const end = start + m[0].length;
        if (selection.from >= start && selection.to <= end) continue;
        decos.push(Decoration.replace({ widget: new LinkPillWidget(m[1], m[2]) }).range(start, end));
      }

      // 4. Project Pills (-> ProjectName)
      const projRegex = /->\s*([A-Za-z0-9_-]+)/g;
      while ((m = projRegex.exec(docText)) !== null) {
        const start = m.index;
        const end = start + m[0].length;
        if (selection.from >= start && selection.to <= end) continue;
        decos.push(Decoration.replace({ widget: new ProjectPillWidget(m[1], onProjectClick) }).range(start, end));
      }

      // 5. Horizontal Rules (--- or ***)
      const hrRegex = /^---$|^\*\*\*$/gm;
      while ((m = hrRegex.exec(docText)) !== null) {
        const start = m.index;
        const end = start + m[0].length;
        if (selection.from >= start && selection.to <= end) continue;
        decos.push(Decoration.replace({ widget: new HRWidget() }).range(start, end));
      }

      // 6. Highlights (==text==)
      const hlRegex = /==([^=]+)==/g;
      while ((m = hlRegex.exec(docText)) !== null) {
        const start = m.index;
        const end = start + m[0].length;
        if (selection.from >= start && selection.to <= end) continue;
        decos.push(Decoration.mark({ class: 'cm-highlight-tag' }).range(start, end));
      }

      // 7. Math ($...$ or $$...$$)
      const mathRegex = /\$\$([\s\S]+?)\$\$|\$([^$\n]+)\$/g;
      while ((m = mathRegex.exec(docText)) !== null) {
        const start = m.index;
        const end = start + m[0].length;
        const isBlock = !!m[1];
        const latex = isBlock ? m[1] : m[2];
        if (selection.from >= start && selection.to <= end) continue;
        decos.push(Decoration.replace({ widget: new MathWidget(latex.trim(), isBlock) }).range(start, end));
      }

      // 8. Cloze cards ({{term}}^c-<id>) — recall feature. Delimiters fold
      // unconditionally (never hand-retyped once minted), unlike bold/italic
      // marks which only fold off the cursor's line — matches root CLAUDE.md's
      // "Cloze braces fold unconditionally" rule.
      const clozeRegex = /\{\{([^{}\n]*?)\}\}(\^c-([a-z0-9]{6}))?/g;
      while ((m = clozeRegex.exec(docText)) !== null) {
        const openFrom = m.index;
        const termFrom = openFrom + 2;
        const termTo = termFrom + m[1].length;
        const closeTo = termTo + 2;
        const idTo = m[2] ? closeTo + m[2].length : closeTo;
        if (m[1].length > 0) {
          decos.push(Decoration.mark({ class: 'cm-cloze' }).range(termFrom, termTo));
        }
        decos.push(Decoration.mark({ class: 'cm-md-marker-cloze' }).range(openFrom, termFrom));
        decos.push(Decoration.mark({ class: 'cm-md-marker-cloze' }).range(termTo, idTo));
      }

      // 9. Recall tag (#recall) — whole-note tagging, same pill-fold rule as cloze.
      const recallTagRegex = /(?<![#\w])#recall(?!\w)/g;
      while ((m = recallTagRegex.exec(docText)) !== null) {
        const start = m.index;
        const end = start + m[0].length;
        if (selection.from >= start && selection.to <= end) continue;
        decos.push(Decoration.replace({ widget: new RecallTagWidget() }).range(start, end));
      }

      // 10. Task reference pill (trailing ^t-<id> on a task line)
      // NOTE: [ \t]* (not \s*) — \s matches \n, which let ^ anchor onto an
      // earlier blank line and made line.from below refer to the wrong line,
      // producing an out-of-range decoration (caught via browser testing).
      const taskLineRegex = /^[ \t]*[-*][ \t]+\[[ xX]\].*$/gm;
      while ((m = taskLineRegex.exec(docText)) !== null) {
        const lineText = m[0];
        const tokenMatch = /\^t-([a-z0-9]{6})\s*$/.exec(lineText);
        if (!tokenMatch) continue;
        const line = view.state.doc.lineAt(m.index);
        if (line.number === cursorLineNum) continue;
        const tokenStart = line.from + tokenMatch.index;
        if (tokenStart >= line.from && tokenStart <= line.to) {
          decos.push(Decoration.replace({ widget: new TaskReferencePillWidget(tokenMatch[1], onTaskRefClick) }).range(tokenStart, line.to));
        }
      }

      // Sort decorations cleanly
      return Decoration.set(decos, true);
    }
  }, {
    decorations: v => v.decorations
  });
}

// Mints missing ^c-<id> (cloze) / ^t-<id> (task reference) anchors, mirroring
// desktop's recall-ids.js / task-ids.js "scheduleMintIds" debounce — waits for
// a pause in typing so the id doesn't visibly pop in mid-keystroke.
function mintMissingIds(view) {
  const text = view.state.doc.toString();
  const usedClozeIds = new Set((text.match(/\^c-[a-z0-9]{6}/g) || []).map(s => s.slice(3)));
  const usedTaskIds = new Set((text.match(/\^t-[a-z0-9]{6}/g) || []).map(s => s.slice(3)));

  function freshId(used) {
    let id;
    do { id = Math.random().toString(36).slice(2, 8).padEnd(6, '0').slice(0, 6); } while (used.has(id));
    used.add(id);
    return id;
  }

  const changes = [];

  const clozeRe = /\{\{([^{}\n]*?)\}\}(\^c-[a-z0-9]{6})?/g;
  let m;
  while ((m = clozeRe.exec(text)) !== null) {
    if (!m[2] && m[1].trim().length > 0) {
      changes.push({ from: m.index + m[0].length, insert: `^c-${freshId(usedClozeIds)}` });
    }
  }

  const taskLineRe = /^[ \t]*[-*][ \t]+\[[ xX]\].*$/gm;
  while ((m = taskLineRe.exec(text)) !== null) {
    const lineText = m[0];
    if (lineText.replace(/^\s*[-*]\s+\[[ xX]\]/, '').trim().length === 0) continue; // empty task, nothing to reference yet
    if (/\^t-[a-z0-9]{6}\s*$/.test(lineText)) continue;
    changes.push({ from: m.index + lineText.length, insert: ` ^t-${freshId(usedTaskIds)}` });
  }

  if (changes.length > 0) {
    view.dispatch({ changes });
  }
}

let mintDebounceTimer = null;
function scheduleMintMissingIds(view) {
  clearTimeout(mintDebounceTimer);
  mintDebounceTimer = setTimeout(() => mintMissingIds(view), 600);
}

// Matches a "-> partial-project-name" fragment ending exactly at the cursor,
// for the project-name autocomplete strip (see app.js's renderAutocompleteStrip).
const PROJECT_QUERY_RE = /->\s*([A-Za-z0-9_-]*)$/;

export function createMobileEditor(parentEl, initialContent, { onChange, onToggleTask, onProjectQuery, onTaskRefClick, onProjectClick, readOnly = false } = {}) {
  const state = EditorState.create({
    doc: initialContent || '',
    extensions: [
      markdown({ extensions: GFM }),
      history(),
      keymap.of([...defaultKeymap, ...historyKeymap]),
      collapsedTaskLinesField,
      collapseRenderField,
      flashLineField,
      createMarkdownLivePlugin(onToggleTask, onTaskRefClick, onProjectClick),
      EditorView.lineWrapping,
      // The swipe ghost renders a note it must never edit, focus, or mint ids
      // into — scheduleMintMissingIds shares one module-level timer across all
      // instances, so a ghost dispatch would clobber the real editor's pending
      // mint and write ^t-/^c- anchors into a note the user isn't editing.
      ...(readOnly ? [EditorState.readOnly.of(true), EditorView.editable.of(false)] : []),
      EditorView.updateListener.of((update) => {
        if (readOnly) return;
        if (update.docChanged && onChange) {
          onChange(update.state.doc.toString());
        }
        if (update.docChanged) {
          scheduleMintMissingIds(update.view);
        }
        if ((update.docChanged || update.selectionSet) && onProjectQuery) {
          const pos = update.state.selection.main.head;
          const line = update.state.doc.lineAt(pos);
          const match = PROJECT_QUERY_RE.exec(line.text.slice(0, pos - line.from));
          onProjectQuery(match ? match[1] : null);
        }
      })
    ]
  });

  const view = new EditorView({ state, parent: parentEl });

  // Inserts the chosen project name in place of the "-> partial" fragment the
  // cursor is currently sitting at — called by app.js's autocomplete strip.
  view.acceptProjectSuggestion = (name) => {
    const pos = view.state.selection.main.head;
    const line = view.state.doc.lineAt(pos);
    const match = PROJECT_QUERY_RE.exec(line.text.slice(0, pos - line.from));
    if (!match) return;
    const from = line.from + match.index;
    const insert = `-> ${name} `;
    view.dispatch({
      changes: { from, to: pos, insert },
      selection: { anchor: from + insert.length }
    });
    view.focus();
  };

  // Scrolls to and briefly highlights the line containing ^t-<taskId> — called
  // by app.js right after switching to the note that owns the referenced task.
  let flashClearTimer = null;
  view.scrollToTaskRef = (taskId) => {
    const text = view.state.doc.toString();
    const tokenPos = text.indexOf(`^t-${taskId}`);
    if (tokenPos === -1) return;
    const line = view.state.doc.lineAt(tokenPos);
    view.dispatch({
      selection: { anchor: line.from },
      effects: [
        EditorView.scrollIntoView(line.from, { y: 'center' }),
        flashLineEffect.of({ pos: line.from })
      ]
    });
    clearTimeout(flashClearTimer);
    flashClearTimer = setTimeout(() => {
      view.dispatch({ effects: flashLineEffect.of(null) });
    }, 1400);
  };

  return view;
}
